// src/components/ImageUpload.jsx
import { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../apiService";
const MAX_MB = 8;

function authHeaders() {
  const token = localStorage.getItem("id_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchAndOpen(id) {
  const res = await fetch(`${BASE_URL}/api/qc/images/${id}/data`, { headers: authHeaders() });
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

function PdfThumb({ id, fileName, onDelete, canEdit }) {
  const [opening, setOpening] = useState(false);

  async function handleClick() {
    setOpening(true);
    try { await fetchAndOpen(id); } catch (_) {}
    finally { setOpening(false); }
  }

  return (
    <div style={{ position: "relative", display: "inline-flex", flexDirection: "column",
      alignItems: "center", gap: 4 }}>
      <div onClick={handleClick}
        style={{ width: 130, height: 100, background: "#fef2f2", borderRadius: 6,
          border: "1px solid #fecaca", display: "flex", flexDirection: "column",
          alignItems: "center", justifyContent: "center", cursor: opening ? "wait" : "pointer",
          gap: 6 }}>
        <span style={{ fontSize: 32, lineHeight: 1 }}>📄</span>
        <span style={{ fontSize: 10, color: "#dc2626", fontWeight: 700, letterSpacing: ".05em" }}>
          {opening ? "Opening…" : "PDF"}
        </span>
      </div>
      <div style={{ fontSize: 10, color: "#6b7280", maxWidth: 130,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        title={fileName}>
        {fileName}
      </div>
      {canEdit && (
        <button onClick={() => onDelete(id)}
          style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)",
            color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20,
            cursor: "pointer", fontSize: 11, lineHeight: "20px", padding: 0, textAlign: "center" }}
          title="Remove">
          ✕
        </button>
      )}
    </div>
  );
}

function ImageThumb({ id, fileName, onDelete, canEdit }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    fetch(`${BASE_URL}/api/qc/images/${id}/data`, { headers: authHeaders() })
      .then(r => r.blob())
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id]);

  return (
    <div style={{ position: "relative", display: "inline-flex", flexDirection: "column",
      alignItems: "center", gap: 4 }}>
      {src ? (
        <img src={src} alt={fileName}
          style={{ width: 130, height: 100, objectFit: "cover",
            borderRadius: 6, border: "1px solid #e5e7eb", cursor: "pointer" }}
          onClick={() => window.open(src, "_blank")}
          title={fileName}
        />
      ) : (
        <div style={{ width: 130, height: 100, background: "#f3f4f6", borderRadius: 6,
          border: "1px solid #e5e7eb", display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: 11, color: "#9ca3af" }}>
          Loading…
        </div>
      )}
      <div style={{ fontSize: 10, color: "#6b7280", maxWidth: 130,
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {fileName}
      </div>
      {canEdit && (
        <button onClick={() => onDelete(id)}
          style={{ position: "absolute", top: 4, right: 4, background: "rgba(0,0,0,0.55)",
            color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20,
            cursor: "pointer", fontSize: 11, lineHeight: "20px", padding: 0, textAlign: "center" }}
          title="Remove">
          ✕
        </button>
      )}
    </div>
  );
}

export function ImageUpload({ entityType, entityId, canEdit }) {
  const [images,    setImages]    = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!entityId) return;
    fetch(`${BASE_URL}/api/qc/images/${entityType}/${entityId}`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setImages(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [entityType, entityId]);

  async function handleFiles(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const tooBig = files.filter(f => f.size > MAX_MB * 1024 * 1024);
    if (tooBig.length) {
      setError(`File${tooBig.length > 1 ? "s" : ""} exceed ${MAX_MB} MB limit: ${tooBig.map(f => f.name).join(", ")}`);
      e.target.value = "";
      return;
    }
    if (images.length + files.length > 10) {
      setError("Maximum 10 attachments per record.");
      e.target.value = "";
      return;
    }

    setUploading(true);
    setError(null);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${BASE_URL}/api/qc/images/${entityType}/${entityId}`, {
          method: "POST",
          headers: authHeaders(),
          body: fd,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.error || "Upload failed");
        }
        const meta = await res.json();
        setImages(old => [...old, { id: meta.id, fileName: meta.fileName, mimeType: meta.mimeType,
          uploadedBy: meta.uploadedBy, uploadedAt: new Date().toISOString() }]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleDelete(id) {
    const item = images.find(i => i.id === id);
    const label = item?.mimeType === "application/pdf" ? "PDF" : "image";
    if (!window.confirm(`Remove this ${label}?`)) return;
    try {
      await fetch(`${BASE_URL}/api/qc/images/${id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      setImages(old => old.filter(img => img.id !== id));
    } catch (err) {
      setError("Delete failed: " + err.message);
    }
  }

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: images.length ? 12 : 0 }}>
        {images.map(img =>
          img.mimeType === "application/pdf"
            ? <PdfThumb key={img.id} id={img.id} fileName={img.fileName}
                onDelete={handleDelete} canEdit={canEdit} />
            : <ImageThumb key={img.id} id={img.id} fileName={img.fileName}
                onDelete={handleDelete} canEdit={canEdit} />
        )}
        {images.length === 0 && (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>No attachments.</div>
        )}
      </div>

      {error && (
        <div className="alert-warn" style={{ marginBottom: 8, fontSize: 12 }}>{error}</div>
      )}

      {canEdit && images.length < 10 && (
        <div style={{ marginTop: 8 }}>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple
            style={{ display: "none" }} onChange={handleFiles} />
          <button type="button" className="btn btn-secondary"
            style={{ fontSize: 12 }}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}>
            {uploading ? "Uploading…" : "+ Add Images / PDF"}
          </button>
          <span style={{ marginLeft: 8, fontSize: 11, color: "#9ca3af" }}>
            JPEG · PNG · PDF · max {MAX_MB} MB each · up to 10 files
          </span>
        </div>
      )}
    </div>
  );
}
